import { useState, useEffect } from 'react';
import '../components/login.css';
import supabase from '../config/supabaseClient'; 
import { useNavigate, useLocation } from 'react-router-dom';

export default function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    setIsLogin(mode !== 'signup');
  }, [location.search]);

  const toggleMode = () => {
    setIsLogin(prev => !prev);
    setIsForgotPassword(false);
    setError(null);
    setMessage(null);
  };

  const resetAlerts = () => {
    setError(null);
    setMessage(null);
  };

  // ─── Forgot Password Handler ───────────────────────────────────────────────
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    resetAlerts();

    if (!email) {
      setError('Please enter your email address.');
      setIsSubmitting(false);
      return;
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/resetpassword`,
    });

    if (error) {
      setError(error.message);
    } else {
      setMessage('✅ Check your email for a password reset link!');
    }

    setIsSubmitting(false);
  };

  // ─── Login / Signup Handler ────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    resetAlerts();

    if (!email || !password) {
      setError('Email and password are required.');
      setIsSubmitting(false);
      return;
    }

    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match.');
      setIsSubmitting(false);
      return;
    }

    try {
      if (isLogin) {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          setError(error.message);
        } else {
          const user = data.user;
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('user_id', user.id)
            .single();

          if (profileError || !profile) {
            console.warn('⚠️ Profile not found:', profileError);
            setError('Login succeeded, but profile not found.');
            setIsSubmitting(false);
            return;
          }

          const first = profile?.first_name || 'User';
          navigate('/dashboard', { state: { firstName: first, lastName: profile.last_name, userId: user.id, email: user.email } });
        }
      } else {
        console.log('🚀 Attempting signup with:', email);
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({ email, password });

        if (signUpError) {
          if (signUpError.message.toLowerCase().includes('user already registered')) {
            setError('An account with that email already exists. Try logging in instead.');
          } else {
            setError(signUpError.message);
          }
          setIsSubmitting(false);
          return;
        }

        if (!signUpData?.user?.id) {
          console.warn('🚨 Early exit: Missing user in signUpData');
          setMessage('🎉 Account created! Please check your email to confirm before logging in.');
          setIsSubmitting(false);
          return;
        }

        const user = signUpData.user;

        console.log('📤 Upserting into profiles with:', {
          user_id: user.id,
          email: user.email ?? null,
          first_name: firstName?.trim() || null,
          last_name: lastName?.trim() || null,
        });

        const { data: insertResult, error: insertError } = await supabase
          .from('profiles')
          .upsert([{
            user_id: user.id,
            email: user.email ?? null,
            first_name: firstName?.trim() || null,
            last_name: lastName?.trim() || null
          }], { onConflict: 'user_id' });

        if (insertError) {
          console.error('🛑 Supabase upsert error:', insertError);
          setError('Insert failed: ' + insertError.message);
          setIsSubmitting(false);
          return;
        }

        console.log('✅ Insert succeeded:', insertResult);
        setMessage('🎉 Minimal insert succeeded!');
      }
    } catch (err) {
      setError('Something went wrong.');
      console.error('Unexpected error:', err);
    }

    setIsSubmitting(false);
  };

  // ─── Forgot Password View ──────────────────────────────────────────────────
  if (isForgotPassword) {
    return (
      <div className="login-container">
        <h2>Reset Password</h2>
        <p style={{ fontSize: '14px', color: '#555', marginBottom: '12px' }}>
          Enter your email and we'll send you a reset link.
        </p>
        <form onSubmit={handleForgotPassword}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>
        {error && <p style={{ color: 'red' }}>{error}</p>}
        {message && <p style={{ color: 'green' }}>{message}</p>}
        <p
          onClick={() => { setIsForgotPassword(false); resetAlerts(); }}
          className="login-toggle"
        >
          ← Back to Login
        </p>
      </div>
    );
  }

  // ─── Login / Signup View ───────────────────────────────────────────────────
  return (
    <div className="login-container">
      <h2>{isLogin ? 'Login' : 'Create Account'}</h2>
      <form onSubmit={handleSubmit}>
        {!isLogin && (
          <>
            <input type="text" placeholder="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            <input type="text" placeholder="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
          </>
        )}
        <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        {!isLogin && (
          <input type="password" placeholder="Confirm Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
        )}
        <button type="submit" disabled={isSubmitting}>
          {isLogin ? 'Login' : 'Sign Up'}
        </button>
      </form>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {message && <p style={{ color: 'green' }}>{message}</p>}
      {isLogin && (
        <p
          onClick={() => { setIsForgotPassword(true); resetAlerts(); }}
          className="login-toggle"
        >
          Forgot your password?
        </p>
      )}
      <p onClick={toggleMode} className="login-toggle">
        {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Login'}
      </p>
    </div>
  );
}